import React, { useState } from 'react';
import {
  TextInput,
  NumberInput,
  Textarea,
  Select,
  Switch,
  DateInput,
  Label,
} from '../ui';
import { useTheme } from '../../contexts/ThemeContext';
import { useCustomFieldValues, useCustomFields, setCustomFieldValue } from '../../modules/custom-fields/useData';
import { useNotification } from '../../contexts/NotificationContext';

interface DynamicFieldRendererProps {
  entityType: string;
  entityId: number | null;
  onValueChange?: (fieldId: number, value: any) => void;
  /** For new entities (no entityId), initial values keyed by field id (parent may not re-render) */
  valuesForNewEntity?: Record<number, any>;
}

const DynamicFieldRenderer: React.FC<DynamicFieldRendererProps> = ({
  entityType,
  entityId,
  onValueChange,
  valuesForNewEntity,
}) => {
  const { colors } = useTheme();
  const { data: fields, isLoading, mutate } = useCustomFieldValues(entityType, entityId);
  const { data: allFields } = useCustomFields(entityType);
  const { showError } = useNotification();
  const displayFields = entityId ? fields : (allFields || []);

  // For new entities, keep local state so inputs stay controlled without parent re-renders
  const [localValues, setLocalValues] = useState<Record<number, any>>(() => valuesForNewEntity ?? {});

  const handleValueChange = async (fieldId: number, value: any) => {
    if (!entityId) {
      setLocalValues((prev) => ({ ...prev, [fieldId]: value }));
      onValueChange?.(fieldId, value);
      return;
    }

    // For existing entities, save immediately
    try {
      await setCustomFieldValue(entityType, entityId, fieldId, value);
      await mutate();
      if (onValueChange) {
        onValueChange(fieldId, value);
      }
    } catch (error: any) {
      showError(error.message || 'Failed to save field value');
    }
  };

  if (isLoading) {
    return <div className={`text-sm ${colors.textMuted}`}>Loading custom fields...</div>;
  }

  if (!displayFields || displayFields.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {displayFields.map((field) => {
        const value = entityId ? field.value : (localValues[field.id] ?? undefined);

        return (
          <div key={field.id}>
            <Label>
              {field.fieldLabel}
              {field.isRequired && <span className="text-red-500 ml-1">*</span>}
            </Label>
            {field.fieldType === 'text' && (
              <TextInput
                value={value || ''}
                onChange={(e) => handleValueChange(field.id, e.target.value)}
                placeholder={`Enter ${field.fieldLabel.toLowerCase()}`}
                required={field.isRequired}
              />
            )}
            {field.fieldType === 'number' && (
              <NumberInput
                value={value || ''}
                onChange={(value) => handleValueChange(field.id, value)}
                placeholder={`Enter ${field.fieldLabel.toLowerCase()}`}
                required={field.isRequired}
                min={field.validation?.min}
                max={field.validation?.max}
              />
            )}
            {field.fieldType === 'date' && (
              <DateInput
                value={value || ''}
                onChange={(value) => handleValueChange(field.id, value)}
                required={field.isRequired}
              />
            )}
            {field.fieldType === 'select' && (
              <Select
                value={value || ''}
                onChange={(e) => handleValueChange(field.id, e.target.value)}
                required={field.isRequired}
                options={[
                  { value: '', label: 'Select an option' },
                  ...(Array.isArray(field.options)
                    ? field.options.map((option) => ({ value: String(option), label: String(option) }))
                    : []),
                ]}
              />
            )}
            {field.fieldType === 'checkbox' && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={value || false}
                  onChange={(e) => handleValueChange(field.id, e.target.checked)}
                />
                <span className={`text-sm ${colors.textSecondary}`}>
                  {value ? 'Yes' : 'No'}
                </span>
              </div>
            )}
            {field.fieldType === 'textarea' && (
              <Textarea
                value={value || ''}
                onChange={(e) => handleValueChange(field.id, e.target.value)}
                placeholder={`Enter ${field.fieldLabel.toLowerCase()}`}
                required={field.isRequired}
                rows={3}
              />
            )}
            {field.validation && (
              <p className={`text-xs ${colors.textMuted} mt-1`}>
                {field.validation.minLength && `Min length: ${field.validation.minLength}`}
                {field.validation.maxLength && ` Max length: ${field.validation.maxLength}`}
                {field.validation.min !== undefined && ` Min: ${field.validation.min}`}
                {field.validation.max !== undefined && ` Max: ${field.validation.max}`}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DynamicFieldRenderer;
